import { DataTableActionBarAction } from '@/components/data-table/data-table-action-bar'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogHeader,
	DialogFooter,
	DialogDescription,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { useBulkUpdateShippingRate, Subscription } from '@/lib/api/subscriptions'
import { useShippingRatesList } from '@/lib/api/shippingRates'
import { Loader2, Truck, Trash2, CheckCircle2 } from 'lucide-react'
import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface UpdateShippingRateBulkActionModalProps {
	subscriptions: Subscription[]
	size?: 'sm' | 'md'
}

interface StoreGroup {
	readonly storeId: string
	readonly storeName: string
	readonly subscriptionIds: readonly string[]
}

const groupSubscriptionsByStore = (subscriptions: Subscription[]): StoreGroup[] =>
	Object.values(
		subscriptions.reduce<Record<string, StoreGroup>>((acc, sub) => {
			const storeId = sub.storeId ?? 'unknown'
			const existing = acc[storeId]
			return {
				...acc,
				[storeId]: {
					storeId,
					storeName: existing?.storeName ?? sub.storeName ?? storeId,
					subscriptionIds: [...(existing?.subscriptionIds ?? []), sub.id],
				},
			}
		}, {}),
	)

const buildShippingRatesQuery = (storeId: string) => ({
	drizzQuery: {
		select: ['id', 'shippingRateName', 'description', 'amount', 'storeId', 'createdAt', 'updatedAt'],
		from: 'shippingRates',
		where: [{
			table: 'shippingRates',
			column: 'storeId',
			operator: 'eq' as const,
			value: storeId,
		}],
		orderBy: [{
			table: 'shippingRates',
			column: 'createdAt',
			direction: 'desc' as const,
		}],
		limit: 100,
		offset: 0,
		joins: [],
	},
})

const StepShippingRateSelector = ({
	storeGroup,
	onUpdate,
	onRemove,
	onCancel,
	isPending,
	t,
}: {
	storeGroup: StoreGroup
	onUpdate: (rateId: string) => void
	onRemove: () => void
	onCancel: () => void
	isPending: boolean
	t: (key: string, options?: Record<string, unknown>) => string
}) => {
	const [selectedRateId, setSelectedRateId] = useState<string | undefined>(undefined)

	const query = useMemo(
		() => buildShippingRatesQuery(storeGroup.storeId),
		[storeGroup.storeId],
	)

	const { data, isLoading } = useShippingRatesList(query, { enabled: true })
	const shippingRates = data?.items ?? []

	const handleUpdate = useCallback(() => {
		if (selectedRateId) onUpdate(selectedRateId)
	}, [selectedRateId, onUpdate])

	return (
		<>
			<div className="space-y-4">
				<div className="space-y-1">
					<p className="text-sm font-medium">
						{t('bulkActions.updateShippingRate.storeLabel', { name: storeGroup.storeName })}
					</p>
					<p className="text-sm text-muted-foreground">
						{t('bulkActions.updateShippingRate.subscriptionCount', {
							count: storeGroup.subscriptionIds.length,
						})}
					</p>
				</div>
				<div className="space-y-2">
					<Label>{t('bulkActions.updateShippingRate.selectShippingRate')}</Label>
					{isLoading ? (
						<div className="flex items-center gap-2 text-sm text-muted-foreground">
							<Loader2 className="size-4 animate-spin" />
							{t('bulkActions.updateShippingRate.loading')}
						</div>
					) : (
						<Select value={selectedRateId} onValueChange={setSelectedRateId}>
							<SelectTrigger>
								<SelectValue placeholder={t('bulkActions.updateShippingRate.selectShippingRate')} />
							</SelectTrigger>
							<SelectContent>
								{shippingRates.length === 0 ? (
									<div className="px-2 py-4 text-center text-sm text-muted-foreground">
										{t('bulkActions.updateShippingRate.noShippingRates')}
									</div>
								) : (
									shippingRates.map((rate) => (
										<SelectItem key={rate.id} value={rate.id}>
											{rate.shippingRateName}
										</SelectItem>
									))
								)}
							</SelectContent>
						</Select>
					)}
				</div>
			</div>
			<DialogFooter className="flex-col gap-2 sm:flex-row">
				<Button
					variant="destructive"
					onClick={onRemove}
					disabled={isPending}
					className="sm:mr-auto"
				>
					{isPending ? (
						<Loader2 className="size-4 animate-spin mr-2" />
					) : (
						<Trash2 className="size-4 mr-2" />
					)}
					{t('bulkActions.updateShippingRate.removeShippingRate')}
				</Button>
				<Button onClick={onCancel} variant="outline">
					{t('bulkActions.updateShippingRate.cancel')}
				</Button>
				<Button onClick={handleUpdate} disabled={isPending || !selectedRateId}>
					{isPending && <Loader2 className="size-4 animate-spin mr-2" />}
					{t('bulkActions.updateShippingRate.update')}
				</Button>
			</DialogFooter>
		</>
	)
}

const SuccessStep = ({
	onClose,
	t,
}: {
	onClose: () => void
	t: (key: string) => string
}) => (
	<>
		<div className="flex flex-col items-center gap-3 py-4">
			<CheckCircle2 className="size-10 text-green-500" />
			<p className="text-sm text-muted-foreground">
				{t('bulkActions.updateShippingRate.successMessage')}
			</p>
		</div>
		<DialogFooter>
			<Button onClick={onClose}>
				{t('bulkActions.updateShippingRate.done')}
			</Button>
		</DialogFooter>
	</>
)

export const UpdateShippingRateBulkActionModal = ({
	subscriptions,
	size = 'sm',
}: UpdateShippingRateBulkActionModalProps) => {
	const { t } = useTranslation('subscriptions')
	const { mutateAsync, isPending } = useBulkUpdateShippingRate()
	const [isOpen, setIsOpen] = useState(false)
	const [currentStep, setCurrentStep] = useState(0)
	const [isComplete, setIsComplete] = useState(false)

	const storeGroups = useMemo(
		() => groupSubscriptionsByStore(subscriptions),
		[subscriptions],
	)

	const currentGroup = storeGroups[currentStep]

	const advanceOrComplete = useCallback(() => {
		if (currentStep < storeGroups.length - 1) {
			setCurrentStep((prev) => prev + 1)
		} else {
			setIsComplete(true)
		}
	}, [currentStep, storeGroups.length])

	const handleUpdate = useCallback(
		async (shippingRateId: string) => {
			if (!currentGroup) return
			await mutateAsync({
				subscriptionIds: [...currentGroup.subscriptionIds],
				shippingRateId,
			})
			advanceOrComplete()
		},
		[currentGroup, mutateAsync, advanceOrComplete],
	)

	const handleRemove = useCallback(async () => {
		if (!currentGroup) return
		await mutateAsync({
			subscriptionIds: [...currentGroup.subscriptionIds],
			shippingRateId: null,
		})
		advanceOrComplete()
	}, [currentGroup, mutateAsync, advanceOrComplete])

	const handleClose = useCallback(() => {
		setIsOpen(false)
		setCurrentStep(0)
		setIsComplete(false)
	}, [])

	const handleOpenChange = (open: boolean) => {
		if (!open && !isPending) handleClose()
	}

	return (
		<>
			{size === 'sm' && (
				<DataTableActionBarAction
					tooltip={t('bulkActions.updateShippingRate.tooltip')}
					isPending={isPending}
					onClick={() => setIsOpen(true)}
				>
					<Truck className="mr-2 h-4 w-4" />
					{t('bulkActions.updateShippingRate.button')}
				</DataTableActionBarAction>
			)}
			{size === 'md' && (
				<Button variant="outline" size="sm" onClick={() => setIsOpen(true)}>
					<Truck className="mr-2 h-4 w-4" />
					{t('bulkActions.updateShippingRate.button')}
				</Button>
			)}
			<Dialog open={isOpen} onOpenChange={handleOpenChange}>
				<DialogContent className="sm:max-w-[500px]">
					<DialogHeader>
						<DialogTitle>{t('bulkActions.updateShippingRate.title')}</DialogTitle>
						{!isComplete && currentGroup && (
							<DialogDescription>
								{t('bulkActions.updateShippingRate.stepIndicator', {
									current: currentStep + 1,
									total: storeGroups.length,
								})}
							</DialogDescription>
						)}
					</DialogHeader>
					{isComplete ? (
						<SuccessStep onClose={handleClose} t={t} />
					) : currentGroup ? (
						<StepShippingRateSelector
							key={currentGroup.storeId}
							storeGroup={currentGroup}
							onUpdate={handleUpdate}
							onRemove={handleRemove}
							onCancel={handleClose}
							isPending={isPending}
							t={t}
						/>
					) : null}
				</DialogContent>
			</Dialog>
		</>
	)
}
